import { ROUTES } from '@/types/common';
import * as NavigateTypes from '@/types/navigate';
import { NATIVE_POST_MESSAGE_TYPE, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import useNavigator from './useNavigator';

/**
 * webview wrapper options
 * safeAreaView / bottomSafeAreaView / keyboardAvoidingView
 * @param webViewType DoctornowWebViewType
 * @default DoctornowWebViewType.BottomSafeAreaView
 *
 * webview options
 * @param scrollEnabled boolean
 * @default true
 *
 * @param bounces boolean
 * @default true
 *
 * @param keyboardDisplayRequiresUserAction boolean
 * @default true
 *
 * @param hideKeyboardAccessoryView boolean
 * @default false
 *
 * bottomSheet options
 * @param hasTelemedicineBottomSheet boolean
 * @default false
 */
const useNavigate = () => {
  const navigator = useNavigator();

  const navigateToBack = () => {
    return navigator.back();
  };

  const navigateToDietInjection = () => {
    return navigator.pushScreen({
      url: ROUTES.DOCTOR.FACE_TO_FACE_PREFERENCE_STEP.path,
    });
  };

  const navigateToFindPharmacy = () => {
    return navigator.pushScreen({
      url: ROUTES.PHARMACIES.PHARMACIES_FIND.pathName,
    });
  };

  const navigateToEmergencyContraceptive = ({ categoryId, type }: NavigateTypes.EmergencyContraceptiveScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.DOCTOR.F2F_LIST.appScreenName,
      params: { categoryId, type },
    });
  };

  const navigateToQnaDetail = ({ questionPid, ...params }: NavigateTypes.QnaDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      params,
    });
  };

  const navigateToNps = () => {
    return navigator.pushScreen({ url: ROUTES.QNA.NPS.path });
  };

  const navigateToHospitalSearch = (params: NavigateTypes.HospitalSearchScreenProps) => {
    return navigator.pushScreen({
      url: ROUTES.HOSPITAL.SEARCH.path,
      params,
    });
  };

  const navigateToMagazineTagList = ({ tagName }: NavigateTypes.MagazineTagListScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.MAGAZINE.TAG.path}?tag=${tagName}`,
    });
  };

  const navigateToHospitalDetail = ({ hospitalPid, ...props }: NavigateTypes.HospitalDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.HOSPITAL.DETAIL.path}/${hospitalPid}`,
      params: {
        webViewType: NavigateTypes.DoctornowWebViewType.KeyboardAvoidingView,
        ...props,
      },
    });
  };

  const navigateToMagazineHome = () => {
    return navigator.pushScreen({
      url: ROUTES.MAGAZINE.HOME.path,
    });
  };

  const navigateToMagazineDetail = ({ magazinePid }: NavigateTypes.MagazineDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.MAGAZINE.DETAIL.path}/${magazinePid}`,
    });
  };

  const navigateToQnaDetailWithJumpToQnaTab = ({ questionPid, ...params }: NavigateTypes.QnaDetailScreenProps) => {
    return navigator.jumpToScreen({
      tabName: 'QuickAnswerTab',
      url: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      params,
    });
  };

  const navigateHomeWithJumpToHomeTab = (params: NavigateTypes.jumpToHomeScreenWithHomeTabProps) => {
    return navigator.jumpToAppScreen({
      tabName: 'HomeTab',
      screenName: ROUTES.HOME.appScreenName,
      params,
    });
  };

  const navigateToTreatmentRequest = (params: NavigateTypes.treatmentRequestAppScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.TREATMENT_REQUEST.TREATMENT_REQUEST.appScreenName,
      params,
    });
  };

  const navigateCategorySelectWithJumpToHomeTab = () => {
    return navigator.jumpToAppScreen({
      tabName: 'HomeTab',
      screenName: ROUTES.DIAGNOSIS.CATEGORY_SELECT.appScreenName,
    });
  };

  const navigateToCategorySelect = () => {
    return navigator.navigateAppScreen({
      screenName: 'CategorySelectScreen',
    });
  };

  const navigateToFamilyRegister = (params: NavigateTypes.FamilyRegisterWebViewScreenProps) => {
    return navigator.pushScreen({
      url: ROUTES.FAMILY.REGISTER.path,
      params: {
        ...params,
        webViewType: NavigateTypes.DoctornowWebViewType.KeyboardAvoidingView,
        keyboardDisplayRequiresUserAction: false,
        hideKeyboardAccessoryView: true,
      },
    });
  };

  const navigateToMedicineDictionaryDetail = ({
    medicineDictionaryPid,
    keyword,
  }: NavigateTypes.MedicineDictionaryDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.MEDICINE_DICTIONARY.DETAIL.path}/${medicineDictionaryPid}/${encodeURIComponent(keyword)}`,
    });
  };

  const navigateToDiseaseEncyclopediaDetail = ({
    diseaseEncyclopediaPid,
    keyword,
  }: NavigateTypes.DiseaseEncyclopediaDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.DISEASE_ENCYCLOPEDIA.DETAIL.path}/${diseaseEncyclopediaPid}/${encodeURIComponent(keyword)}`,
    });
  };

  const navigateToMagazineAuthorDetail = ({ authorPublicId }: NavigateTypes.MagazineAuthorDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.AUTHOR.DETAIL.path}/${authorPublicId}`,
    });
  };

  const navigateToQnaTagList = ({ questionTagId, tagName }: NavigateTypes.QnaTagListScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.QNA.TAG.path}/${questionTagId}?tagName=${tagName}`,
    });
  };

  const navigateToQnaList = (params?: NavigateTypes.QnaListScreenProps) => {
    return navigator.pushScreen({
      url: ROUTES.QNA.REALTIME.path,
      params,
    });
  };

  const navigateToHomeSearchResult = (params?: NavigateTypes.HomeSearchScreenProps) => {
    return navigator.pushScreen({
      url: ROUTES.SEARCH(params?.keyword, params?.tab).SEARCH_RESULT.path,
      params,
    });
  };

  const navigateToQnaWrite = () => {
    return navigator.pushScreen({
      url: ROUTES.QNA.WRITE.path,
    });
  };

  const navigateToPureWebViewAppScreen = (params: NavigateTypes.PureWebViewAppScreenProps) => {
    return navigator.pushAppScreen({
      screenName: 'PureWebViewScreen',
      params: {
        uri: params.uri,
        headerTitle: params.headerTitle ?? '',
      },
    });
  };

  const navigateToExternalBrowser = ({ url }: { url: string }) => {
    return navigator.openExternalBrowser({ url });
  };

  const navigateMedicineDictionaryDetail = ({ url }: { url: string }) => {
    return navigator.pushScreen({
      url,
    });
  };

  const navigatePharmacyReviewAppScreen = (params: NavigateTypes.PharmacyReviewAppScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.PHARMACIES.PHARMACIES_REVIEW.appScreenName,
      params: {
        pharmacyPid: params.pharmacyPid,
        longitude: params.longitude,
        latitude: params.latitude,
      },
    });
  };

  const navigateToNonBenefitTreatmentMedicineDetail = ({
    treatmentMedicineId,
  }: NavigateTypes.NonBenefitTreatmentMedicineDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.NON_BENEFIT.TREATMENT_MEDICINE_DETAIL.path}/${treatmentMedicineId}`,
    });
  };

  const navigateToKakaoPayWebViewScreen = ({ url, transactionId }: NavigateTypes.KakaoPayWebViewScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.KAKAOPAY.HOME.appScreenName,
      params: { url, transactionId },
    });
  };

  const navigateToDoctorDetail = (params: NavigateTypes.DoctorDetailScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.DOCTOR.DETAIL.appScreenName,
      params,
    });
  };

  const navigateToHospitalEventPage = (params: NavigateTypes.HospitalEventPageProps) => {
    return navigator.pushScreen({
      url: ROUTES.HOSPITAL.EVENT.path,
      params,
    });
  };

  const navigateToPharmacyDetail = ({ pharmacyId, ...params }: NavigateTypes.PharmacyDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.PHARMACIES.PHARMACIES_DETAIL.path}/${pharmacyId}`,
      params,
    });
  };

  const navigateToDoctorListAppScreen = (params: NavigateTypes.DoctorListAppScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.DOCTOR.LIST.appScreenName,
      params,
    });
  };

  const navigateToDiagnosisCategorySelectAppScreen = () => {
    return navigator.pushAppScreen({
      screenName: ROUTES.DIAGNOSIS.CATEGORY_SELECT.appScreenName,
    });
  };

  const navigateToPrescriptionSendImageCheckAppScreen = (
    params: NavigateTypes.PrescriptionSendImageCheckAppScreenProps
  ) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.PHARMACIES.PRESCRIPTION_SEND_IMAGE_CHECK.appScreenName,
      params,
    });
  };

  const navigateToPointHistory = () => {
    return navigator.pushAppScreen({
      screenName: ROUTES.POINT_MARKET.HISTORY.appScreenName,
    });
  };

  const navigateToPointMarketList = () => {
    return navigator.pushAppScreen({
      screenName: ROUTES.POINT_MARKET.LIST.appScreenName,
    });
  };

  const navigateToMyInvitationCodeScreen = () => {
    return navigator.pushAppScreen({
      screenName: ROUTES.MY_INVITATION_CODE.appScreenName,
    });
  };

  const navigateToPointMarketCertificationScreen = (params?: NavigateTypes.PointMarketCertificationScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.POINT_MARKET.CERTIFICATION.appScreenName,
      params,
    });
  };

  const navigateToPrescriptionSendFaxInputWebViewScreen = (
    params: NavigateTypes.PrescriptionSendFaxInputWebViewScreenProps
  ) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.PRESCRIPTION.FAX_INPUT.appScreenName,
      params,
    });
  };

  const navigateToPrescriptionReselectReasonWebViewScreen = (
    params: NavigateTypes.PrescriptionReselectReasonWebViewScreenProps
  ) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.PRESCRIPTION.RESELECT_REASON.appScreenName,
      params,
    });
  };

  const navigateToSearchRequestDetailInfo = (params: NavigateTypes.SearchRequestDetailInfoProps) => {
    return navigator.pushScreen({
      url: ROUTES.SEARCH().REQUEST_DETAIL_INFO.path,
      params,
    });
  };

  const navigateToPrescriptionFailWebViewScreen = (params: NavigateTypes.PrescriptionFailWebViewScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.PRESCRIPTION.FAIL.appScreenName,
      params,
    });
  };

  const navigateToPrescriptionSendFaxCheckWebViewScreen = (
    params: NavigateTypes.PrescriptionSendFaxCheckWebViewScreenProps
  ) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.PRESCRIPTION.FAX_CHECK.appScreenName,
      params,
    });
  };

  const navigateToAdditionalOrder = (params: NavigateTypes.AdditionalOrderWebViewScreenProps) => {
    return navigator.pushScreen({
      url: ROUTES.ADDITIONAL_ORDER.path,
      params: {
        ...params,
        mainTagTypes: JSON.stringify(params.mainTagTypes),
        medicineRequestInfos: JSON.stringify(params.medicineRequestInfos),
      },
    });
  };

  const navigateToSelectedScreenWithResetNavigation = (
    params: NavigateTypes.SelectedScreenWithResetNavigationProps
  ) => {
    return sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.RESET_NAVIGATION_TO_SELECTED_SCREEN, {
      screenName: params.screenName,
    });
  };

  const navigateToPaymentScreen = (params: NavigateTypes.PaymentScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.PAYMENT.appScreenName,
      params,
    });
  };

  const navigateToSplashAppScreen = () => {
    return navigator.pushAppScreen({
      screenName: ROUTES.COMMON.SPLASH.appScreenName,
    });
  };

  const navigateToUpdateAppScreen = () => {
    return navigator.pushAppScreen({
      screenName: ROUTES.COMMON.UPDATE.appScreenName,
    });
  };

  const navigateToF2FDoctorListAppScreen = (params: NavigateTypes.F2FDoctorListAppScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.DOCTOR.F2F_LIST.appScreenName,
      params,
    });
  };

  const navigateToRegisterCardScreen = () => {
    return navigator.pushScreen({
      url: ROUTES.CARD.REGISTER.path,
    });
  };

  const navigateToKakaoPayScreen = ({ url, transactionId }: NavigateTypes.KakaoPayWebViewScreenProps) => {
    return navigator.pushScreen({
      url: url,
      params: { transactionId },
    });
  };

  const navigateToTelemedicineRequestScreen = ({ categoryId, type }: NavigateTypes.TelemedicineRequestScreenProps) => {
    return navigator.pushScreen({
      url: ROUTES.TELEMEDICINE.REQUEST.path,
      params: { categoryId, type },
    });
  };

  const navigateToTelemedicineCategoriesScreen = () => {
    return navigator.pushScreen({
      url: ROUTES.TELEMEDICINE.CATEGORIES.path,
    });
  };

  const navigateToSearchHomeScreen = ({ keyword, tab }: NavigateTypes.SearchHomeScreenProps) => {
    navigator.pushScreen({
      url: ROUTES.SEARCH().HOME.path,
      params: { keyword, tab },
    });
  };

  return {
    navigateToBack,
    navigateToSplashAppScreen,
    navigateToUpdateAppScreen,
    navigateToFindPharmacy,
    navigateToEmergencyContraceptive,
    navigateToQnaDetail,
    navigateToNps,
    navigateToHospitalSearch,
    navigateToMagazineTagList,
    navigateToHospitalDetail,
    navigateToMagazineHome,
    navigateToMagazineDetail,
    navigateToQnaDetailWithJumpToQnaTab,
    navigateHomeWithJumpToHomeTab,
    navigateToTreatmentRequest,
    navigateCategorySelectWithJumpToHomeTab,
    navigateToCategorySelect,
    navigateToFamilyRegister,
    navigateToDietInjection,
    navigateToMedicineDictionaryDetail,
    navigateToMagazineAuthorDetail,
    navigateToDiseaseEncyclopediaDetail,
    navigateToQnaTagList,
    navigateToQnaList,
    navigateToHomeSearchResult,
    navigateToQnaWrite,
    navigateToPureWebViewAppScreen,
    navigateMedicineDictionaryDetail,
    navigatePharmacyReviewAppScreen,
    navigateToExternalBrowser,
    navigateToNonBenefitTreatmentMedicineDetail,
    navigateToKakaoPayWebViewScreen,
    navigateToDoctorDetail,
    navigateToHospitalEventPage,
    navigateToPharmacyDetail,
    navigateToDoctorListAppScreen,
    navigateToDiagnosisCategorySelectAppScreen,
    navigateToPrescriptionSendImageCheckAppScreen,
    navigateToPointHistory,
    navigateToPointMarketList,
    navigateToMyInvitationCodeScreen,
    navigateToPointMarketCertificationScreen,
    navigateToPrescriptionSendFaxInputWebViewScreen,
    navigateToPrescriptionReselectReasonWebViewScreen,
    navigateToSearchRequestDetailInfo,
    navigateToPrescriptionFailWebViewScreen,
    navigateToPrescriptionSendFaxCheckWebViewScreen,
    navigateToAdditionalOrder,
    navigateToSelectedScreenWithResetNavigation,
    navigateToPaymentScreen,
    navigateToF2FDoctorListAppScreen,
    navigateToRegisterCardScreen,
    navigateToKakaoPayScreen,
    navigateToTelemedicineRequestScreen,
    navigateToTelemedicineCategoriesScreen,
    navigateToSearchHomeScreen,
  };
};

export default useNavigate;
